import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, Spin, notification, Button, Space, Select, Typography } from 'antd';
import { ProductPropertiesDTO, AccountMappingDTO, AccountMappingType } from '@/shared/api/model';
import { useInternalAccounts } from '@/modules/internal-account/hooks/useInternalAccounts';
import useAccountMappings from '../hooks/useAccountMappings';

const { Text } = Typography;
const { Option } = Select;

interface GLAccountMappingsPanelProps {
  productGlNo: string;
  initialData: ProductPropertiesDTO;
}

// Mapping type display names
const mappingTypeLabels: Record<string, string> = {
  PRINCIPAL: 'Customer Loan Receivable Account',
  INTEREST: 'Interest GL Account',
  INTEREST_CAPITALIZATION: 'Interest Capitalization GL Account',
  PENALTY: 'Penalty GL Account',
  PROVISION_BAD_DEBT_CREDIT: 'Provision for Bad Debts (Credit)',
  PROVISION_BAD_DEBT_DEBIT: 'Provision for Bad Debts (Debit)',
  LOAN_WRITTEN_OFF_DEBIT: 'Loan Written-off (Debit)',
  LOAN_WRITTEN_OFF_CREDIT: 'Loan Written-off (Credit)',
  INTEREST_RECEIVABLE: 'Interest to be Received (Debit)',
  UNEARNED_INTEREST_INCOME: 'Unearned Interest Income (Credit)',
  LOAN_TAKEOVER_PAYMENT: 'Loan Takeover Payment (Credit)',
  TOP_UP_CLEARING: 'Top-Up Clearing Account',
};

const GLAccountMappingsPanel: React.FC<GLAccountMappingsPanelProps> = ({ 
  productGlNo,
  initialData
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Get account mappings for this product
  const {
    accountMappings,
    isLoading: isLoadingMappings,
    saveAccountMapping,
    saveAllAccountMappings
  } = useAccountMappings({ productGlNo });

  // Get internal accounts for selection
  const { 
    accounts: internalAccounts,
    isLoading: isLoadingInternalAccounts
  } = useInternalAccounts();

  // Loading state
  const isLoading = isLoadingMappings || isLoadingInternalAccounts;

  // Function to find internal account by GL and AC
  const findInternalAccount = (glNo: string, acNo: string) => {
    return internalAccounts?.find(
      account => account.glNo === glNo && account.acNo === acNo
    );
  };

  // Get account mapping by type
  const getAccountMappingByType = (type: string): AccountMappingDTO | undefined => {
    return accountMappings?.find(mapping => mapping.mappingType === type);
  };

  // Convert form values to account mappings
  const formValuesToMappings = (values: any): AccountMappingDTO[] => {
    return Object.entries(values).map(([key, value]: [string, any]) => {
      const existingMapping = getAccountMappingByType(key);
      const internalAccount = internalAccounts?.find(
        account => `${account.glNo}-${account.acNo}` === value
      );
      
      return {
        id: existingMapping?.id,
        mappingType: key as AccountMappingType,
        name: mappingTypeLabels[key] || key,
        description: `Account mapping for ${mappingTypeLabels[key] || key}`,
        glNo: internalAccount?.glNo || '',
        acNo: internalAccount?.acNo || '',
        active: true,
        productPropertiesGlNo: productGlNo
      };
    }).filter(mapping => mapping.glNo && mapping.acNo);
  };

  // Prepare initial form values
  useEffect(() => {
    if (accountMappings?.length > 0) {
      const initialValues: Record<string, string> = {};
      
      accountMappings.forEach(mapping => {
        if (mapping.glNo && mapping.acNo) {
          initialValues[mapping.mappingType] = `${mapping.glNo}-${mapping.acNo}`;
        }
      });
      
      form.setFieldsValue(initialValues);
    }
  }, [accountMappings, form]);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    try {
      setSaving(true);
      const mappings = formValuesToMappings(values);
      
      // Save each mapping
      for (const mapping of mappings) {
        await saveAccountMapping(mapping);
      }
      
      notification.success({
        message: 'Success',
        description: 'GL Account mappings updated successfully',
      });
    } catch (error) {
      console.error('Failed to update GL Account mappings:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to update GL Account mappings. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Render internal account options
  const renderInternalAccountOptions = () => {
    return internalAccounts?.map(account => (
      <Option key={`${account.glNo}-${account.acNo}`} value={`${account.glNo}-${account.acNo}`}>
        <div>
          <Text strong>{account.acName}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>
            ({account.glNo}-{account.acNo})
          </Text>
        </div>
      </Option>
    )) || [];
  };

  // Render account mapping form item
  const renderAccountMappingFormItem = (mappingType: string, label: string) => {
    return (
      <Col xs={24} sm={24} md={8} key={mappingType}>
        <Card title={label} size="small">
          <Form.Item name={mappingType} label="Select Account">
            <Select
              showSearch
              placeholder="Select an internal account"
              optionFilterProp="children"
              filterOption={(input, option) => {
                const children = option?.children as any;
                return children?.props?.children?.[0]?.props?.children?.toString().toLowerCase().includes(input.toLowerCase()) ||
                       children?.props?.children?.[1]?.props?.children?.[1]?.toString().toLowerCase().includes(input.toLowerCase());
              }}
              style={{ width: '100%' }}
            >
              {renderInternalAccountOptions()}
            </Select>
          </Form.Item>
        </Card>
      </Col>
    );
  };

  return (
    <Spin spinning={isLoading || saving}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <div className="gl-account-mappings">
          <Row gutter={[16, 16]}>
            {Object.entries(mappingTypeLabels).map(([type, label]) => 
              renderAccountMappingFormItem(type, label)
            )}
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save GL Account Mappings
              </Button>
            </Space>
          </Row>
        </div>
      </Form>
    </Spin>
  );
};

export default GLAccountMappingsPanel;
